import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useAppData } from '../data';
import { isProActive } from '../data/selectors';
import { PaywallSheet } from './PaywallSheet';

type ProContextValue = {
  /** Active subscriber OR within the 7-day trial. */
  isPro: boolean;
  /** True iff the trial is the source of `isPro` (not a real purchase). */
  isInTrial: boolean;
  /** Whole days remaining in the trial; 0 if no trial / expired. */
  trialDaysLeft: number;
  /** Show the comparison + trial sheet. */
  openPaywall: () => void;
  /** Hide it. */
  closePaywall: () => void;
};

const ProContext = createContext<ProContextValue | null>(null);

export function useProContext(): ProContextValue {
  const ctx = useContext(ProContext);
  if (!ctx) {
    throw new Error('useProContext must be used inside <ProProvider>');
  }
  return ctx;
}

const TRIAL_MS = 7 * 24 * 60 * 60 * 1000;

export function ProProvider({ children }: { children: ReactNode }) {
  const data = useAppData();
  const [open, setOpen] = useState(false);

  const isPro = isProActive(data);
  const isInTrial = !!data.proTrial && !data.proPurchased && isPro;

  const trialDaysLeft = useMemo(() => {
    if (!data.proTrial) return 0;
    const started = Date.parse(data.proTrial.started);
    if (!Number.isFinite(started)) return 0;
    return Math.max(
      0,
      Math.ceil((started + TRIAL_MS - Date.now()) / (24 * 60 * 60 * 1000)),
    );
  }, [data.proTrial]);

  const openPaywall = useCallback(() => setOpen(true), []);
  const closePaywall = useCallback(() => setOpen(false), []);

  const value = useMemo<ProContextValue>(
    () => ({ isPro, isInTrial, trialDaysLeft, openPaywall, closePaywall }),
    [isPro, isInTrial, trialDaysLeft, openPaywall, closePaywall],
  );

  return (
    <ProContext.Provider value={value}>
      {children}
      <PaywallSheet open={open} onClose={closePaywall} />
    </ProContext.Provider>
  );
}
